<script lang="ts" setup>
import type { ProductDetailItem } from "~~/models/products";
import type { CatalogPageRecord } from "~~/server/graphql/datocms/generated";
import { z } from "zod";
import { baseUrl } from "~~/config/canonicalSettings";
import { roundIconButtonUi } from "~/utils/styles";

const { trackProductDetail } = useGtmTracking();

const { locale } = useI18n();
const localePath = useLocalePath();
const { loggedIn, user } = useUserSession();

const layout = await useLayout();
const cmsData = await useCatalogPage();

if (!layout || !cmsData) {
  throw create404Error();
}

const categories = await useCategories();

const schema = z.object({ id: z.coerce.number() });
const { id: productId } = schema.parse(useRoute().params);

const { data: product } = await useFetch<ProductDetailItem>("/api/ecom/products/detail", {
  query: {
    warehouseId: user.value?.warehouseId,
    productIdList: productId,
    addCategories: true,
    addExternalData: true,
    addFiles: true,
    addOverpacks: true,
    addOverpackTranslations: true,
    addProductRelationsOnlyOnlineProducts: true,
    addProductTranslations: true,
    addPropertyValues: true,
    addStoreData: true,
    locale: locale.value, // Use the current locale (the detector would fallback to the default locale at SSR)
  },
  watch: [locale],
});

if (!product.value) {
  throw create404Error();
}

// only load prices when authenticated
const { prices, isPricesLoading } = usePricing(product, { loggedIn: loggedIn.value });

const { activeOverpack, activePrice, update: updateOverpack } = useOverpack(product, prices);

const extraSpecifications: Array<{ label?: string; value?: string }> = [
  {
    label: cmsData.value?.detailLabelVariableWeight,
    value: product.value.isVariableWeight ? cmsData.value?.detailLabelYes : cmsData.value?.detailLabelNo,
  },
  {
    label: cmsData.value?.detailLabelOrderProduct,
    value: product.value.categoryInfo.isBackorderProduct ? cmsData.value?.detailLabelYes : cmsData.value?.detailLabelNo,
  },
];

useProductSlugsToI18n(product.value);

useHead({
  title: () => product.value?.name || null,
});

useSeoMeta({
  title: product.value?.name,
  description: product.value?.description,
  ogImageUrl: product.value.mainImage?.absoluteUrl,
  ogUrl: `https://www.spuntini.be/${localePath(product.value.detailUrl)}`,
});

useSchemaOrg([
  defineProduct({
    brand: {
      name: product.value?.dataLayer?.brand ?? "",
    },
    category: product.value?.dataLayer?.category,
    // gtin13: product.value?.ean,
    image: [product.value?.mainImage?.absoluteUrl ?? `${baseUrl}/spuntini/logo/mail.png`],
    name: product.value?.name,
    sku: product.value?.number,
    offers: {
      price: 0,
      priceCurrency: "EUR",
    },
  }),
]);

const activeImageIsOpen = ref(false);
const activeImageIdx = ref(0);
const activeImage = computed(() => product.value?.sortedImages?.at(activeImageIdx.value));

function openModal(activeIdx: number) {
  activeImageIdx.value = activeIdx;
  activeImageIsOpen.value = true;
}

async function onDownloadTechnicalSheet() {
  if (!loggedIn.value) {
    return;
  }

  if (product.value) {
    $fetch("/api/ecom/deepdive/product-document-download", {
      method: "POST",
      body: {
        productId: product.value.id,
        documentUrl: product.value.technicalSheetUrl,
      },
    });
  }
}

watch(() => activePrice.value, () => {
  if (!product.value) {
    return;
  }

  const activeDiscount = (activePrice.value?.originalPrice ?? 0) - (activePrice.value?.salesPrice ?? 0);
  trackProductDetail(product.value.dataLayer, activePrice.value?.salesPrice, activeDiscount, activePrice.value?.currencyCode);
});

onMounted(async () => {
  if (product.value && loggedIn.value) {
    $fetch("/api/ecom/deepdive/product-detail-view", {
      method: "POST",
      body: {
        productId: product.value.id,
      },
    });
  }
});
</script>

<template>
  <div v-if="cmsData && product">
    <ProductCatalogHeader
      view-mode="details"
      :cms-data="cmsData as CatalogPageRecord"
      :category-tree="categories.tree"
      :category="categories.tree.getCategory(product.productGroupCategoryId ?? 0)"
    />

    <div class="my-lg gap-xl flex flex-col">
      <div class="gap-lg flex flex-col">
        <div>
          <h1>{{ product.name }}</h1>
          <span class="text-sm font-medium text-neutral-300">{{ cmsData.detailLabelProductNumber }}: {{ product.number }}</span>
        </div>

        <div class="gap-md flex flex-col rounded-lg bg-white lg:flex-row">
          <div v-if="product.sortedImages?.length" class="lg:my-lg shrink-0">
            <UModal v-model:open="activeImageIsOpen" :dismissible="false" :ui="{ content: 'min-w-[95%] lg:min-w-[75%] xl:min-w-[35%]' }">
              <ClientOnly>
                <ProductImageWrapper
                  :is-frozen-product="product.isFrozenProduct"
                  :is-limited="product.isLimited"
                  :is-new="product.isNew"
                  :is-sale="product.isSale"
                  :is-variable-weight="product.isVariableWeight"
                  :show="{ iconLabel: true, zoom: true }"
                >
                  <ProductImageSwiper
                    :product="product"
                    thumb-swiper-id="main-thumb-swiper"
                    @main-image-click="openModal"
                  />
                </ProductImageWrapper>
              </ClientOnly>

              <template #content>
                <UCard
                  :ui="{
                    root: 'ring-0',
                    header: 'text-primary text-base font-semibold px-1 py-1 sm:px-1',
                  }"
                >
                  <template #header>
                    <div class="flex items-start justify-end">
                      <div>
                        <UButton variant="solid" icon="i-ph-x" :ui="roundIconButtonUi" @click="activeImageIsOpen = false" />
                      </div>
                    </div>
                  </template>

                  <div class="flex items-center justify-center">
                    <NuxtImg
                      provider="bunny"
                      placeholder="/img/placeholder.svg"
                      :src="activeImage?.relativeUrl"
                      :alt="product.name ?? ''"
                      :title="product.name ?? ''"
                      class="h-350px object-cover sm:h-[450px] lg:h-[550px]"
                    />
                  </div>
                </UCard>
              </template>
            </UModal>
          </div>

          <div v-else class="lg:my-lg shrink-0">
            <UModal>
              <ProductImageWrapper
                :is-frozen-product="product.isFrozenProduct"
                :is-limited="product.isLimited"
                :is-new="product.isNew"
                :is-sale="product.isSale"
                :is-variable-weight="product.isVariableWeight"
                :show="{ iconLabel: true, zoom: product.mainImage !== null && product.mainImage !== undefined }"
                class="size-[350px] lg:size-[545px]"
              >
                <ProductImage
                  :image-url="product.mainImage?.relativeUrl"
                  :image-alt="product.name"
                  class="h-[260px] object-cover lg:h-[400px]"
                />
              </ProductImageWrapper>

              <template #content>
                <UCard
                  :ui="{
                    root: 'ring-0',
                    header: 'text-primary text-base font-semibold px-1 py-1 sm:px-1',
                  }"
                >
                  <template #header>
                    <div class="flex items-start justify-end">
                      <div>
                        <UButton variant="solid" icon="i-ph-x" :ui="roundIconButtonUi" @click="activeImageIsOpen = false" />
                      </div>
                    </div>
                  </template>

                  <div class="flex items-center justify-center">
                    <NuxtImg
                      provider="bunny"
                      placeholder="/img/placeholder.svg"
                      :src="activeImage?.relativeUrl"
                      :alt="product.name ?? ''"
                      :title="product.name ?? ''"
                      class="h-350px object-cover sm:h-[450px] lg:h-[550px]"
                    />
                  </div>
                </UCard>
              </template>
            </UModal>
          </div>

          <div class="grow">
            <div class="p-md lg:p-xl gap-md lg:gap-xl flex flex-col items-start">
              <p v-if="product.description?.length" class="text-balance" v-html="product.description" />

              <div v-if="!loggedIn" class="gap-md lg:gap-xl flex flex-col">
                <div>
                  {{ cmsData.detailLabelOverpack }}: {{ product.defaultOverpack?.singleName }}
                </div>

                <ProductCallToAction :cms-data="cmsData" />
              </div>

              <div v-if="loggedIn" class="gap-md lg:gap-xl flex w-full flex-col">
                <div class="flex">
                  <ProductOrderBadge v-if="product.categoryInfo.isBackorderProduct" />
                </div>

                <ProductPrice
                  view-mode="details"
                  :price="activePrice" :is-prices-loading="isPricesLoading"
                  :is-variable-weight="product.isVariableWeight ?? false"
                />

                <ProductAddToBasket
                  :cms-data="cmsData"
                  :product="product"
                  :overpack="activeOverpack"
                  :overpack-popper="{ placement: 'bottom' }"
                  :add-to-basket="{
                    show: { button: true, label: true },
                    class: 'w-full md:w-auto',
                    containerClass: 'justify-start',
                    listName: 'Details',
                  }"
                  :add-to-template="{ show: { button: true } }"
                  :replacements="{ show: true }"
                  @updated-overpack="updateOverpack"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="p-xl gap-lg flex flex-col rounded-lg bg-[#F3F1EC]">
        <h3>{{ cmsData.detailTitleSpecifications }}</h3>

        <div class="gap-y-md lg:gap-y-lg gap-x-5xl flex flex-col lg:flex-row">
          <div class="gap-y-md lg:gap-y-lg flex flex-col">
            <div
              v-for="specification of product.specifications"
              :key="specification.label"
              class="gap-x-lg gap-y-2xs grid grid-cols-1 lg:grid-cols-2"
            >
              <div class="font-semibold">
                {{ specification.label }}
              </div>
              <div>{{ specification.value }}</div>
            </div>
          </div>

          <div class="gap-y-md lg:gap-y-lg flex flex-col">
            <div
              v-for="specification of extraSpecifications"
              :key="specification.label"
              class="gap-x-lg gap-y-2xs grid grid-cols-1 lg:grid-cols-2"
            >
              <div class="font-semibold">
                {{ specification.label }}
              </div>
              <div>{{ specification.value }}</div>
            </div>
          </div>
        </div>

        <div>
          <UButton
            v-if="product.technicalSheetUrl"
            variant="outline" color="primary" size="md"
            :to="product.technicalSheetUrl" target="_blank"
            icon="i-ph-file-arrow-down" trailing
            @click="onDownloadTechnicalSheet"
          >
            {{ cmsData.detailActionDownloadTechnicalSheet }}
          </UButton>
        </div>
      </div>
    </div>

    <LazyProductCardRelated
      v-if="product.productGroupCategoryId"
      :layout="layout!" :cms-data="cmsData" :product="product"
    />

    
  </div>
</template>

<style scoped>

</style>
